import * as React from 'react';

import { useAssessmentContext } from '../../context';
import useDebounce from '../../hooks/useDebounce';
import IAddress from '../../models/IAddress';
import IAssessmentElement from '../../models/IAssessmentElement';
import IOption from '../../models/IOption';
import { IValidatorConfig, IValidatorResult } from '../../models/IValidator';
import AssessmentLocalStorageManager from '../../plugins/AssessmentLocalStorageManager';
import useAddressLookupManager from '../../plugins/useAddressLookupManager';
import useQuestionManager from '../../plugins/useQuestionManager';
import SmartSearchWithLoadMore from '../inputs/SmartSearchWithLoadMore';

interface IProps 
{
	question: IAssessmentElement,
	onValidatorResultChanged?(validatorResult: IValidatorResult): void,
	onResetValidation?(parentElement: IAssessmentElement | null): void,
	validatorConfig?: IValidatorConfig
}

export function AddressSearchLookup(props: IProps)
{
	const questionManager = useQuestionManager();
	const addressLookupManager = useAddressLookupManager();
	const assessmentContext = useAssessmentContext();

	const [assessmentId] = React.useState<string>(assessmentContext.assessment?.assessmentId ?? '');
	const [answerValues, setAnswerValues] = React.useState<string[]>([]);
	const [selectedOption, setSelectedOption] = React.useState<IOption | null>(null);
	const [options, setOptions] = React.useState<IOption[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isLoadingMore, setIsLoadingMore] = React.useState(false);
	const [searchFilter, setSearchFilter] = React.useState<string>('');
	const [continuationToken, setContinuationToken] = React.useState<string | undefined>(undefined);
	const [hasMore, setHasMore] = React.useState<boolean>(false);

	// Debounce the search filter
	const debouncedSearchFilter = useDebounce(searchFilter, 500);

	// Effect to perform search when debounced filter changes
	React.useEffect(() =>
	{
		const performSearch = async (filter: string) =>
		{
			if ((filter?.length ?? 0) === 0)
			{
				setOptions([]);
				setIsLoading(false);
				setContinuationToken(undefined);
				setHasMore(false);
				return;
			}

			setIsLoading(true);
			try
			{
				const result = await addressLookupManager.searchAddresses(filter);

				setOptions(result.options);
				setContinuationToken(result.continuationToken);
				setHasMore(!!result.continuationToken?.trim());


				setIsLoading(false);
			}
			catch (error)
			{
				console.error('Error loading addresses:', error);
				setOptions([]);
				setContinuationToken(undefined);
				setHasMore(false);
				setIsLoading(false);
			}
		};

		performSearch(debouncedSearchFilter);
	},
	[debouncedSearchFilter, addressLookupManager]);

	React.useEffect(() =>
	{
		// Load existing answer if present
		const existingAnswers = assessmentContext.pageAnswers.filter(x => x.id === props.question.id)[0]?.value ?? [];
		setAnswerValues(existingAnswers);

		// Use the stored filter value to retrieve the initial options
		const addressFilter = AssessmentLocalStorageManager.getAnswerValues(assessmentId, props.question.id, 'Filter');

		if (addressFilter.length > 0)
		{
			try
			{
				const storedOption = JSON.parse(addressFilter[0]) as IOption;
				setSelectedOption(storedOption);
			}
			catch (error)
			{
				console.error('Failed to parse stored option:', error);
			}
		}
	},
	[
		assessmentId,
		assessmentContext.pageAnswers,
		props.question.id
	]);

	const handleLoadMore = React.useCallback(async () =>
	{
		if (!continuationToken || isLoadingMore || !searchFilter)
			return;

		setIsLoadingMore(true);
		try
		{
			const result = await addressLookupManager.searchAddresses(searchFilter, continuationToken);

			// Append new options to existing ones
			setOptions(prevOptions => [...prevOptions, ...result.options]);
			setContinuationToken(result.continuationToken);
			setHasMore(!!result.continuationToken?.trim());

			setIsLoadingMore(false);
		}
		catch (error)
		{
			console.error('Error loading more addresses:', error);
			setIsLoadingMore(false);
		}
	}, [
		continuationToken,
		isLoadingMore,
		searchFilter,
		addressLookupManager
	]);

	const handleChangeFilter = React.useCallback((filter: string) =>
	{
		setSearchFilter(filter);
		// Reset pagination when search filter changes
		setContinuationToken(undefined);
		setHasMore(false);
	}, []);

	const onValueChange = (newValue: IOption | null) =>
	{
		const newAnswerValue = newValue?.value ?? '';
		setAnswerValues([newAnswerValue]);
		questionManager.updateAnswerValue(props.question.id, [newAnswerValue]);
		setSelectedOption(newValue);

		// Save the displayed text of the selected option for loading on page refresh
		AssessmentLocalStorageManager.setAnswerValues(
			assessmentId,
			props.question.id,
			[JSON.stringify(newValue)],
			'Filter'
		);

		// Populate related fields if address is selected
		if (newValue && newValue.data)
		{
			const selectedAddress = newValue.data as IAddress;
			const addressMappings = props.question.metadata?.addressMappings ?? [];

			if (addressMappings && addressMappings.length > 0)
			{
				addressLookupManager.populateAddressFields(
					assessmentId,
					selectedAddress,
					addressMappings,
					assessmentContext.assessment
				);
			}
		}

		if (props.onResetValidation && props.question.hasConditionalChildren)
			props.onResetValidation(props.question);
	};

	return (
		<SmartSearchWithLoadMore
			onValidatorResultChanged={props.onValidatorResultChanged}
			validator={{
				validatorRules: questionManager.getQuestionValidatorRules(props.question),
				config: props.validatorConfig
			}}
			value={selectedOption}
			answerValue={answerValues}
			options={options}
			onValueChange={onValueChange}
			onInputChange={handleChangeFilter}
			isLoading={isLoading}
			onLoadMore={handleLoadMore}
			hasMore={hasMore}
			isLoadingMore={isLoadingMore}
			outerLabel="Search for an address"
			helpText="Start typing to search for addresses"
		/>
	);
}

export default AddressSearchLookup;
